import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Users,
  AlertTriangle,
  Check,
  X,
  Clock,
  Loader2,
  Info
} from 'lucide-react';
import DriverSelector from './DriverSelector';
import ConflictDetection from './ConflictDetection';
import AssignmentHistory from './AssignmentHistory';
import vehicleService from '../../services/vehicleService';

const normalizeId = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  return String(value);
};

const normalizeDriverForAssignment = (driver = {}, vehicleId, vehicleNumber) => {
  const normalizedVehicleId = normalizeId(vehicleId);
  let assignedVehicles = Array.isArray(driver.assignedVehicles)
    ? driver.assignedVehicles.map(normalizeId).filter(Boolean)
    : [];

  if (!assignedVehicles.length && Array.isArray(driver.vehicleAssignments)) {
    assignedVehicles = driver.vehicleAssignments
      .map((assignment) => normalizeId(assignment?.vehicleId ?? assignment))
      .filter(Boolean);
  }

  const potentialVehicleIds = [
    driver.currentVehicleId,
    driver.vehicleId,
    driver.activeVehicleId,
    driver.primaryVehicleId
  ]
    .map(normalizeId)
    .filter(Boolean);

  const potentialVehicleNumbers = [
    driver.currentVehicle,
    driver.vehicleNumber,
    driver.activeVehicle,
    driver.primaryVehicle
  ].filter(Boolean);

  if (normalizedVehicleId) {
    const matchesCurrentVehicle =
      potentialVehicleIds.includes(normalizedVehicleId) ||
      (vehicleNumber && potentialVehicleNumbers.includes(vehicleNumber));

    if (matchesCurrentVehicle && !assignedVehicles.includes(normalizedVehicleId)) {
      assignedVehicles = [...assignedVehicles, normalizedVehicleId];
    }
  }

  const currentVehicle =
    potentialVehicleNumbers[0] ||
    potentialVehicleIds[0] ||
    (assignedVehicles.includes(normalizedVehicleId) ? vehicleNumber : null) ||
    driver.currentVehicle ||
    null;

  const rawStatus = (driver.status || '').toString().toLowerCase();
  const availabilityStatus = (driver.availabilityStatus || driver.availability || '')
    .toString()
    .toLowerCase();

  let assignmentStatus = 'available';
  const isOnLeave =
    rawStatus.includes('leave') ||
    rawStatus === 'inactive' ||
    rawStatus === 'terminated' ||
    availabilityStatus.includes('leave') ||
    availabilityStatus === 'inactive';

  if (isOnLeave) {
    assignmentStatus = 'on-leave';
  } else if (availabilityStatus === 'busy') {
    assignmentStatus = 'busy';
  } else {
    const assignedElsewhere = assignedVehicles.some(
      (id) => id && id !== normalizedVehicleId
    );

    if (
      assignedElsewhere ||
      (currentVehicle &&
        vehicleNumber &&
        currentVehicle !== vehicleNumber &&
        !assignedVehicles.includes(normalizedVehicleId))
    ) {
      assignmentStatus = 'assigned';
    } else if (assignedVehicles.includes(normalizedVehicleId)) {
      assignmentStatus = 'assigned';
    }
  }

  return {
    ...driver,
    assignedVehicles,
    currentVehicle,
    assignmentStatus,
    originalStatus: rawStatus,
    availabilityStatus,
    status: assignmentStatus
  };
};

const VehicleAssignment = ({
  vehicleId,
  vehicleNumber,
  currentDriverId,
  companyId,
  drivers = [],
  driversLoading = false,
  driversError = null,
  onAssignmentChange,
  onRefreshDrivers,
  className = ''
}) => {
  const [selectedDriverId, setSelectedDriverId] = useState(currentDriverId || null);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [assignmentError, setAssignmentError] = useState(null);
  const [assignmentSuccess, setAssignmentSuccess] = useState(null);

  const normalizedDrivers = useMemo(
    () =>
      (drivers || []).map((driver) =>
        normalizeDriverForAssignment(driver, vehicleId, vehicleNumber)
      ),
    [drivers, vehicleId, vehicleNumber]
  );

  const findDriverById = useCallback(
    (id) => {
      const normalizedId = normalizeId(id);
      if (!normalizedId) {
        return undefined;
      }

      return normalizedDrivers.find(
        (driver) => normalizeId(driver.id) === normalizedId
      );
    },
    [normalizedDrivers]
  );

  const computeConflicts = useCallback(
    (driver) => {
      if (!driver) {
        return [];
      }

      const issues = [];
      const normalizedVehicleId = normalizeId(vehicleId);

      if (driver.assignmentStatus === 'on-leave') {
        issues.push({
          type: 'unavailable',
          severity: 'error',
          message: `${driver.name} is currently unavailable for assignment.`
        });
      }

      const assignedElsewhere =
        Array.isArray(driver.assignedVehicles) &&
        driver.assignedVehicles.some(
          (id) => id && id !== normalizedVehicleId
        );

      if (assignedElsewhere) {
        const otherVehicles = driver.assignedVehicles
          .filter((id) => id && id !== normalizedVehicleId)
          .join(', ');
        issues.push({
          type: 'already_assigned',
          severity: 'warning',
          message: `${driver.name} is currently assigned to vehicle(s): ${otherVehicles}`
        });
      }

      if (
        driver.currentVehicle &&
        vehicleNumber &&
        driver.currentVehicle !== vehicleNumber &&
        !assignedElsewhere
      ) {
        issues.push({
          type: 'already_assigned',
          severity: 'warning',
          message: `${driver.name} is currently assigned to ${driver.currentVehicle}`
        });
      }

      const licenseClass = (driver.licenseClass || driver.cdlClass || '')
        .toString()
        .toUpperCase();
      if (vehicleNumber?.startsWith('T-') && licenseClass && !licenseClass.includes('A')) {
        issues.push({
          type: 'license_mismatch',
          severity: 'error',
          message: `${driver.name} has ${licenseClass} but this vehicle typically requires CDL-A`
        });
      }

      return issues;
    },
    [vehicleId, vehicleNumber]
  );

  const normalizedSelectedDriverId = normalizeId(selectedDriverId);
  const normalizedCurrentDriverId = normalizeId(currentDriverId);

  useEffect(() => {
    setSelectedDriverId(currentDriverId || null);
  }, [currentDriverId]);

  useEffect(() => {
    const driver = findDriverById(normalizedSelectedDriverId);
    if (driver) {
      setConflicts(computeConflicts(driver));
    } else {
      setConflicts([]);
    }
  }, [normalizedSelectedDriverId, findDriverById, computeConflicts]);

  useEffect(() => {
    const now = new Date().toISOString().slice(0, 10);

    if (!normalizedCurrentDriverId) {
      setAssignmentHistory((prev) =>
        prev.map((entry) =>
          entry.status === 'active'
            ? {
                ...entry,
                status: 'completed',
                endDate: entry.endDate || now,
                endReason: entry.endReason || 'Unassigned'
              }
            : entry
        )
      );
      return;
    }

    setAssignmentHistory((prev) => {
      const alreadyActive = prev.some(
        (entry) =>
          entry.status === 'active' &&
          normalizeId(entry.driverId) === normalizedCurrentDriverId
      );

      if (alreadyActive) {
        return prev;
      }

      const driver = findDriverById(normalizedCurrentDriverId);

      const closedPrev = prev.map((entry) =>
        entry.status === 'active'
          ? {
              ...entry,
              status: 'completed',
              endDate: entry.endDate || now,
              endReason: entry.endReason || 'Reassigned'
            }
          : entry
      );

      const newEntry = {
        id: `${normalizedCurrentDriverId}-${now}`,
        driverId: normalizedCurrentDriverId,
        driverName: driver?.name || 'Assigned Driver',
        startDate: now,
        endDate: null,
        status: 'active',
        totalMiles: driver?.totalMiles ?? null,
        averageMPG: driver?.averageMPG ?? null,
        safetyScore: driver?.safetyScore ?? null,
        endReason: null
      };

      return [newEntry, ...closedPrev];
    });
  }, [normalizedCurrentDriverId, findDriverById]);

  const handleDriverSelect = useCallback(
    (driverId) => {
      setAssignmentError(null);
      setAssignmentSuccess(null);
      setSelectedDriverId(driverId);

      const driver = findDriverById(driverId);
      if (driver) {
        setConflicts(computeConflicts(driver));
      } else {
        setConflicts([]);
      }
    },
    [findDriverById, computeConflicts]
  );

  const handleAssignDriver = useCallback(async () => {
    if (!normalizedSelectedDriverId) {
      return;
    }

    if (!companyId || !vehicleId) {
      setAssignmentError('Company and vehicle context are required for assignment.');
      return;
    }

    if (conflicts.some((conflict) => conflict.severity === 'error')) {
      return;
    }

    setIsProcessing(true);
    setAssignmentError(null);
    setAssignmentSuccess(null);

    try {
      const updatedVehicle = await vehicleService.updateDriverAssignment(
        vehicleId,
        normalizedSelectedDriverId,
        { companyId }
      );

      const driver = findDriverById(normalizedSelectedDriverId);
      const now = new Date().toISOString().slice(0, 10);

      setAssignmentHistory((prev) => {
        const closedPrev = prev.map((entry) =>
          entry.status === 'active'
            ? {
                ...entry,
                status: 'completed',
                endDate: entry.endDate || now,
                endReason: entry.endReason || 'Reassigned'
              }
            : entry
        );

        const newEntry = {
          id: `${normalizedSelectedDriverId}-${now}`,
          driverId: normalizedSelectedDriverId,
          driverName: driver?.name || 'Assigned Driver',
          startDate: now,
          endDate: null,
          status: 'active',
          totalMiles: driver?.totalMiles ?? null,
          averageMPG: driver?.averageMPG ?? null,
          safetyScore: driver?.safetyScore ?? null,
          endReason: null
        };

        return [newEntry, ...closedPrev];
      });

      setAssignmentSuccess(
        `Assigned ${driver?.name || 'driver'} to vehicle ${vehicleNumber || vehicleId}.`
      );
      setConflicts([]);

      onAssignmentChange?.({
        driverId: normalizedSelectedDriverId,
        driverName: findDriverById(normalizedSelectedDriverId)?.name || null,
        vehicle: updatedVehicle
      });

      if (typeof onRefreshDrivers === 'function') {
        await Promise.resolve(onRefreshDrivers());
      }
    } catch (error) {
      console.error('Driver assignment failed:', error);
      setAssignmentError(
        error.getUserFriendlyMessage?.() ||
          error.message ||
          'Failed to assign driver. Please try again.'
      );
    } finally {
      setIsProcessing(false);
    }
  }, [
    companyId,
    vehicleId,
    normalizedSelectedDriverId,
    conflicts,
    findDriverById,
    vehicleNumber,
    onAssignmentChange,
    onRefreshDrivers
  ]);

  const handleUnassignDriver = useCallback(async () => {
    if (!normalizedCurrentDriverId) {
      return;
    }

    if (!companyId || !vehicleId) {
      setAssignmentError('Company and vehicle context are required to unassign.');
      return;
    }

    setIsProcessing(true);
    setAssignmentError(null);
    setAssignmentSuccess(null);

    try {
      const updatedVehicle = await vehicleService.updateDriverAssignment(
        vehicleId,
        null,
        { companyId }
      );

      const now = new Date().toISOString().slice(0, 10);

      setAssignmentHistory((prev) =>
        prev.map((entry) =>
          entry.status === 'active'
            ? {
                ...entry,
                status: 'completed',
                endDate: entry.endDate || now,
                endReason: entry.endReason || 'Unassigned'
              }
            : entry
        )
      );

      setSelectedDriverId(null);
      setConflicts([]);
      setAssignmentSuccess('Driver unassigned successfully.');

      onAssignmentChange?.({
        driverId: null,
        vehicle: updatedVehicle
      });

      if (typeof onRefreshDrivers === 'function') {
        await Promise.resolve(onRefreshDrivers());
      }
    } catch (error) {
      console.error('Driver unassignment failed:', error);
      setAssignmentError(
        error.getUserFriendlyMessage?.() ||
          error.message ||
          'Failed to unassign driver. Please try again.'
      );
    } finally {
      setIsProcessing(false);
    }
  }, [
    normalizedCurrentDriverId,
    companyId,
    vehicleId,
    onAssignmentChange,
    onRefreshDrivers
  ]);

  const canAssign = Boolean(
    normalizedSelectedDriverId &&
      normalizedSelectedDriverId !== normalizedCurrentDriverId &&
      !conflicts.some((conflict) => conflict.severity === 'error') &&
      !isProcessing &&
      companyId &&
      vehicleId
  );

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Users className="h-6 w-6 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Driver Assignment</h3>
        </div>
        {normalizedCurrentDriverId && (
          <button
            onClick={handleUnassignDriver}
            disabled={isProcessing}
            className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            <X className="h-4 w-4 mr-1" />
            Unassign Driver
          </button>
        )}
      </div>

      {driversError && !driversLoading && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 flex items-start space-x-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <span>{driversError}</span>
        </div>
      )}

      {assignmentError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 flex items-start space-x-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <span>{assignmentError}</span>
        </div>
      )}

      {assignmentSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 flex items-start space-x-2">
          <Check className="h-4 w-4 mt-0.5" />
          <span>{assignmentSuccess}</span>
        </div>
      )}

      {companyId ? null : (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 text-sm rounded-lg px-4 py-3 flex items-start space-x-2">
          <Info className="h-4 w-4 mt-0.5" />
          <span>Select a company to manage driver assignments.</span>
        </div>
      )}

      {driversLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          Loading drivers...
        </div>
      ) : (
        <DriverSelector
          drivers={normalizedDrivers}
          selectedDriverId={selectedDriverId}
          currentDriverId={currentDriverId}
          onDriverSelect={handleDriverSelect}
        />
      )}

      {conflicts.length > 0 && <ConflictDetection conflicts={conflicts} />}

      {normalizedSelectedDriverId && normalizedSelectedDriverId !== normalizedCurrentDriverId && (
        <div className="flex justify-end">
          <button
            onClick={handleAssignDriver}
            disabled={!canAssign}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Assign Driver
              </>
            )}
          </button>
        </div>
      )}

      <AssignmentHistory assignments={assignmentHistory} />
    </div>
  );
};

export default VehicleAssignment;
